import { Download, Tag } from 'lucide-react'
import type { ArtifactResponse } from '@/client/types.gen'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/hooks/useTranslation'

export interface ArtifactPreviewHeaderActionsProps {
	artifact: ArtifactResponse
	onEdit: () => void
	onDownload: () => Promise<void>
	downloading: boolean
	hideDownload?: boolean
	downloadArtifactLabel?: string
}

export function ArtifactPreviewHeaderActions({
	artifact,
	onEdit,
	onDownload,
	downloading,
	hideDownload = false,
	downloadArtifactLabel,
}: ArtifactPreviewHeaderActionsProps) {
	const { t } = useTranslation()

	const downloadLabel =
		downloadArtifactLabel ?? t('pages.projectDetail.artifactPreview.downloadRawFile')

	const actionButtons = (
		<>
			<Button
				type="button"
				variant="outline"
				size="sm"
				onClick={onEdit}
				aria-label={t('pages.projectDetail.artifactPreview.editMetadata')}
				className="shrink-0 min-w-0 p-2 lg:px-3 lg:py-2"
			>
				<Tag className="size-4 shrink-0 lg:mr-2" aria-hidden />
				<span className="hidden lg:inline">
					{t('pages.projectDetail.artifactPreview.editMetadata')}
				</span>
			</Button>
			{!hideDownload && (
				<Button
					type="button"
					variant="outline"
					size="sm"
					onClick={onDownload}
					disabled={downloading || artifact.status === 'pending_upload'}
					aria-label={downloadLabel}
					className="shrink-0 min-w-0 p-2 lg:px-3 lg:py-2"
				>
					{downloading ? (
						<Spinner className="size-4 shrink-0 lg:mr-2 animate-spin" aria-hidden />
					) : (
						<Download className="size-4 shrink-0 lg:mr-2" aria-hidden />
					)}
					<span className="hidden lg:inline">{downloadLabel}</span>
				</Button>
			)}
		</>
	)

	return <div className="flex items-center gap-3 shrink-0">{actionButtons}</div>
}
